import { useState } from 'react';

import { useHistory } from 'react-router-dom';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Button,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { dialogSx } from './styles';
import { type Mission } from 'src/utils/type';
import { ROUTES } from 'src/utils/constant';
import { approveSubmissions } from 'src/api/apis';
import { TokenExpiredError } from 'src/api/error';
import SubmissionCard from 'src/components/Card/Submission';

export default function SubmissionsDialog ({
  show,
  users,
  mission,
  onClose,
  onSuccess,
}: {
  show: boolean;
  users: any[];
  mission: Mission;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const [selectedSubmissions, setSelectedSubmissions] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const total = users.length;
  const start = currentPage * pageSize;
  const end = Math.min((currentPage + 1) * pageSize, total);
  const maxPage = Math.floor((total - 1) / pageSize);
  const shownUsers = users.slice(start, end);

  const toggleCheckbox = (submissionId: string) => {
    if (selectedSubmissions.includes(submissionId)) {
      setSelectedSubmissions(selectedSubmissions.filter(id => id !== submissionId));
    } else {
      setSelectedSubmissions([...selectedSubmissions, submissionId]);
    }
  };

  const onApprove = () => {
    approveSubmissions(selectedSubmissions)
      .then(_ => {
        setSelectedSubmissions([]);
        setCurrentPage(0);
        onClose();
        onSuccess();
      })
      .catch(err => {
        if (err instanceof TokenExpiredError) {
          history.push(ROUTES.login);
        }
      });
  };

  const onSelectAll = () => {
    setSelectedSubmissions(users.map(user => user.submissions[0]._id));
  };
  return (
    <Dialog open={show} sx={dialogSx}>
      <DialogTitle>
        {t('Approve Submission')}
        <i className="bi bi-x" onClick={onClose} />
      </DialogTitle>
      <DialogContent>
        <Box marginBottom="25px" display="flex" justifyContent="space-between">
          <div style={{ fontSize: '19px', color: '#006EC2', fontWeight: 'bold', lineHeight: '40px' }}>
            {mission?.title.en}
          </div>
          <Box>
            <div
              style={{
                fontSize: '19px',
                color: '#006EC2',
                fontWeight: 'bold',
                lineHeight: '40px',
                display: 'inline-block',
              }}
            >
              {t('Selected')}: {selectedSubmissions.length}/{total}
            </div>
            <Button size="small" onClick={onSelectAll} sx={{ marginLeft: '30px' }}>
              {t('Select All')}
            </Button>
          </Box>
        </Box>
        <Box display="flex" flexWrap="wrap" columnGap="25px" rowGap="15px" justifyContent="center">
          {shownUsers?.map((user, id) => (
            <SubmissionCard
              key={id}
              user={user}
              onToggle={toggleCheckbox}
              selected={selectedSubmissions.includes(user.submissions[0]._id)}
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onApprove} disabled={selectedSubmissions.length === 0}>
          {t('Approve')}
        </Button>
        <Box display="flex" alignItems="center">
          <div style={{ display: 'inline-block', marginRight: '10px' }}>
            <div style={{ lineHeight: '40px', height: '40px', display: 'inline-block', margin: '8px' }}>
              {t('Items per page')}:
            </div>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={pageSize}
                onChange={e => {
                  setCurrentPage(0);
                  setPageSize(Number(e.target.value));
                }}
              >
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div style={{ display: 'inline-block' }}>{`${start + 1}-${end} of ${total}`}</div>
          <div style={{ display: 'inline-block', marginLeft: '10px' }}>
            <KeyboardArrowLeftIcon
              width={30}
              height={30}
              onClick={() => { setCurrentPage(prev => Math.max(prev - 1, 0)); }}
              sx={{ fill: currentPage === 0 ? '#C4C4C4' : '#000000' }}
            />
            <KeyboardArrowRightIcon
              width={30}
              height={30}
              onClick={() => { setCurrentPage(prev => Math.min(prev + 1, maxPage)); }}
              sx={{ fill: currentPage === maxPage ? '#C4C4C4' : '#000000' }}
            />
          </div>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
